import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { EntityKeys, QueryType } from 'utils/reactQuery'
import { logger } from 'utils/logging'
import { useAPIV2 } from 'utils/API'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import type { MeasurementPricingDTO } from 'models/purchaseFlow/measurementPricingDTO'
import type { MeasurementQuoteRequestDTO, MeasurementQuoteResponseDTO } from 'models/purchaseFlow/measurementQuoteDTO'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

/**
 * On-Site Area Report pricing for PF v2.
 *
 * Both endpoints are session-scoped, like everything else in v2: the backend
 * resolves the client, the country, the VAT rate and the revenue channel from
 * the purchase session, so nothing the caller sends can select a pricebook.
 * That is the reason this swap mattered rather than being tidy-up — the v1 pair
 * identified the client by an `email` query param and resolved the channel from
 * the CALLER's session, so a Propstack / OneSystem client was quoted off the
 * base book while placement billed their channel book (the same class of defect
 * as ISSUE-3012), and an admin preview priced off the base book too.
 *
 * `ws` / `sessionId` are resolved INTERNALLY (store + UserDataContext + auth0),
 * never passed by call sites, so every consumer shares one cache entry — the
 * same shape as `useInstructionOptions` and `useCatalogue`. Both hooks self-gate
 * on `ws && sessionId`, so neither can fire before the session exists.
 *
 * Why the quote exists at all: since ISSUE-2188 the engine DERIVES the measured
 * extras from the configuration instead of attaching them to the cart, so a
 * measurement line's catalogue price is the base carrier alone and can be less
 * than half of what placement bills. The client cannot see their own price
 * without asking the engine.
 *
 * The v1 `/order/configuration/measurement/{quote,pricing}` pair stays for the
 * legacy step; nothing here deprecates it.
 */
enum Endpoints {
  /** POST — priced estimate for one measurement configuration. */
  SESSION_MEASUREMENT_QUOTE = '/workspace/{workspaceId}/sessions/{sessionId}/measurement/quote',
  /** GET — the Salesforce-fed rate card the estimate and the hints both read. */
  SESSION_MEASUREMENT_PRICING = '/workspace/{workspaceId}/sessions/{sessionId}/measurement/pricing',
}

/**
 * Workspace + session for the v2 measurement endpoints. Admin → the picked
 * target workspace, client → its property workspace: the same resolution
 * `useCatalogue` and `useInstructionOptions` use, so all three agree on scope
 * and the backend's session↔workspace guard never sees a mismatch.
 */
const useMeasurementScope = () => {
    const sessionId = usePfStore((s) => s.draft.sessionId)

  const ws = usePfSessionWorkspaceId()

  return { ws, sessionId }
}

/**
 * The rate card, for the per-option price hints.
 *
 * Country, VAT and channel come from the session, so an admin ordering on a
 * client's behalf passes nothing — unlike the v1 endpoint, where an admin query
 * stayed idle until a target client's email was known.
 */
export function usePfMeasurementPricing(isEnabled = true) {
  const api = useAPIV2<Endpoints>()
  const { ws, sessionId } = useMeasurementScope()

  return useQuery({
    queryKey: [EntityKeys.MEASUREMENT_PRICING, QueryType.GET, { ws, sessionId, pf: 'v2' }],
    queryFn: async () => {
      if (!ws || !sessionId) throw new Error('measurement rate card called without a session')

      try {
        return await api.get<MeasurementPricingDTO>(
          Endpoints.SESSION_MEASUREMENT_PRICING,
          { workspaceId: ws, sessionId },
          true
        )
      } catch (error) {
        logger.error('Failed to fetch measurement rate card', error as Error, {
          context: { component: 'usePfMeasurementPricing' },
        })
        throw error
      }
    },
    enabled: isEnabled && Boolean(ws && sessionId),
  })
}

/**
 * The priced estimate for a configuration.
 *
 * Guarded on the two inputs the engine cannot price without, so an in-progress form
 * does not fire a request that can only fail.
 */
export function usePfMeasurementQuote(payload: MeasurementQuoteRequestDTO | null) {
  const api = useAPIV2<Endpoints>()
  const { ws, sessionId } = useMeasurementScope()

  return useQuery({
    queryKey: [EntityKeys.MEASUREMENT_QUOTE, QueryType.GET, { ...payload, ws, sessionId, pf: 'v2' }],
    queryFn: async () => {
      if (!ws || !sessionId) throw new Error('measurement quote called without a session')

      try {
        return await api.post<MeasurementQuoteResponseDTO>(
          Endpoints.SESSION_MEASUREMENT_QUOTE,
          { workspaceId: ws, sessionId },
          payload ?? {},
          true
        )
      } catch (error) {
        logger.error('Failed to fetch measurement quote', error as Error, {
          context: { component: 'usePfMeasurementQuote', metadata: { ...payload } },
        })
        throw error
      }
    },
    // A config change is a new key: keep the last amounts on screen while the fresh
    // ones load, so the panel updates in place instead of collapsing.
    placeholderData: keepPreviousData,
    enabled: !!payload && payload.surfaceArea > 0 && payload.numberOfFloors > 0 && Boolean(ws && sessionId),
  })
}
